import { useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  type Node,
  type Edge,
  type NodeTypes,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box, Chip, Typography } from '@mui/material';
import type { EntityRelationshipDto } from '../../api/queries/relationships';

interface Props {
  entityId: string;
  entityName: string;
  relationships: EntityRelationshipDto[];
  onNavigate?: (entityId: string) => void;
}

function EntityNode({
  data,
}: {
  data: { label: string; type: string; isCurrent: boolean };
}) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        border: 2,
        borderColor: data.isCurrent ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: data.isCurrent ? 'action.selected' : 'background.paper',
        minWidth: 120,
        textAlign: 'center',
        cursor: data.isCurrent ? 'default' : 'pointer',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ visibility: 'hidden' }}
      />
      <Typography
        variant="subtitle2"
        noWrap
        fontWeight={data.isCurrent ? 700 : 400}
      >
        {data.label}
      </Typography>
      <Chip
        label={data.type}
        size="small"
        sx={{ fontSize: 10, height: 18, mt: 0.5 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ visibility: 'hidden' }}
      />
    </Box>
  );
}

const nodeTypes: NodeTypes = { entityNode: EntityNode };

export function RelationshipGraph({
  entityId,
  entityName,
  relationships,
  onNavigate,
}: Props) {
  const { nodes, edges } = useMemo(() => {
    const nodeMap = new Map<
      string,
      { id: string; name: string; type: string }
    >();
    nodeMap.set(entityId, { id: entityId, name: entityName, type: 'current' });

    for (const rel of relationships) {
      if (rel.entityAId === entityId) {
        nodeMap.set(rel.entityBId, {
          id: rel.entityBId,
          name: rel.entityBName ?? rel.entityBId,
          type: '',
        });
      } else {
        nodeMap.set(rel.entityAId, {
          id: rel.entityAId,
          name: rel.entityAName ?? rel.entityAId,
          type: '',
        });
      }
    }

    // Position nodes in a circle around the center
    const entries = Array.from(nodeMap.values());
    const centerX = 300;
    const centerY = 200;
    const radius = 180;

    const ns: Node[] = entries.map((entry, i) => {
      const isCurrent = entry.id === entityId;
      const angle = isCurrent
        ? 0
        : ((i - 1) / (entries.length - 1)) * 2 * Math.PI - Math.PI / 2;
      return {
        id: entry.id,
        type: 'entityNode',
        position: isCurrent
          ? { x: centerX, y: centerY }
          : {
              x: centerX + radius * Math.cos(angle),
              y: centerY + radius * Math.sin(angle),
            },
        data: { label: entry.name, type: entry.type, isCurrent },
      };
    });

    const es: Edge[] = relationships.map((rel) => ({
      id: rel.id,
      source: rel.entityAId,
      target: rel.entityBId,
      label: rel.relationshipType.replace(/_/g, ' '),
      type: 'smoothstep',
      animated: false,
      style: { strokeWidth: 2 },
      labelStyle: { fontSize: 11 },
    }));

    return { nodes: ns, edges: es };
  }, [entityId, entityName, relationships]);

  const onNodeClick = useCallback(
    (_: unknown, node: Node) => {
      if (node.id !== entityId && onNavigate) {
        onNavigate(node.id);
      }
    },
    [entityId, onNavigate],
  );

  if (relationships.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="body2" color="text.secondary">
          No relationships yet. Add one to see the graph.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ height: 450, border: 1, borderColor: 'divider', borderRadius: 1 }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </Box>
  );
}
